import { defineStore, acceptHMRUpdate } from 'pinia'
import { DateTime } from 'luxon'
import { type Sendable } from '@/interfaces/Sendable'
import {
  Checklist,
  type TDehydratedChecklist,
} from '@/classes/sendables/project/Checklist'
import {
  Documentation,
  type TDehydratedDocumentation,
} from '@/classes/sendables/project/Documentation'
import {
  Confirmation,
  type TDehydratedConfirmation,
} from '@/classes/sendables/project/Confirmation'
import {
  ProjectCompletion,
  type TDehydratedProjectCompletion,
} from '@/classes/sendables/project/ProjectCompletion'
import {
  ProjectDateCompletion,
  type TDehydratedProjectDateCompletion,
} from '@/classes/sendables/project/ProjectDateCompletion'
import { Video, type TDehydratedVideo } from '@/classes/sendables/project/Video'
import { Network } from '@capacitor/network'
import type { TDehydratedTravelLog } from '@/classes/sendables/TravelLog'
import { TravelLog } from '@/classes/sendables/TravelLog'

type TState = {
  queue: Sendable[]
}
const initialState: TState = {
  queue: [],
}
const MAX_RETRIES = 10

const useServerQueue = defineStore('serverQueue', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    isEmpty(state): boolean {
      return state.queue.length === 0
    },
    sentAllReadyItems(state): boolean {
      return state.queue.every((i) => !i.isReady())
    },
  },
  actions: {
    add(item: Sendable) {
      if (this.queue.includes(item)) {
        console.log('ServerQueue: item already in queue')
        this.sync()
        return
      }
      console.log('ServerQueue: Adding item: ', item)
      this.queue.push(item)
      console.log('ServerQueue: Current queue length: ', this.queue.length)
      this.sync()
    },
    sync() {
      console.log('ServerQueue: attempting to sync...')
      void Network.getStatus().then(async (status) => {
        if (status.connected) {
          for (const item of this.queue) {
            if (
              !item.meta.transmitted ||
              item.meta.error_log.length < MAX_RETRIES
            ) {
              console.log('ServerQueue: checking if item is ready', item)
              try {
                if (!item.isReady()) {
                  console.log('Item not ready to be sent', item)
                  continue
                }
                console.log('Item ready', item)
                console.log('ServerQueue: attempting to send Item: ', item)
                const result = await item.send()
                if (result.error) {
                  item.meta.error_log.push({
                    error_message: result.error.error_message,
                    error_code: result.error.error_code,
                  })
                  console.error(
                    'ServerQueue: Failed to send Item: ',
                    result.error,
                    item,
                  )
                  continue
                } else {
                  item.meta.transmitted = true
                  item.meta.transmitted_at = DateTime.now().toISO()
                  item.cleanup()
                }
              } catch (error) {
                item.meta.error_log.push({
                  error_message: '',
                  error_code: 'SERVER_QUEUE',
                })
                console.error('ServerQueue: Failed to send Item: ', error, item)
              }
            }
          }
        } else {
          console.error('ServerQueue: No network connection, syncing delayed.')
        }
        this.prune()
      })
    },
    prune() {
      console.log('ServerQueue: Pruning queue...', this.queue)
      this.queue = this.queue.filter(
        (item) =>
          !item.meta.transmitted && item.meta.error_log.length < MAX_RETRIES,
      )
      console.log('ServerQueue: Prune complete', this.queue)
    },

    remove(item: Sendable) {
      console.log('ServerQueue: Removing item: ', item)
      this.queue = this.queue.filter((i) => i !== item)
      console.log('ServerQueue: Current queue length: ', this.queue.length)
    },

    hydrate(state: string) {
      const hydrationMap: { [k: string]: (o: object) => Sendable } = {
        checklist: (c) => Checklist.fromJSON(c as TDehydratedChecklist),
        confirmation: (c) =>
          Confirmation.fromJSON(c as TDehydratedConfirmation),
        documentation: (d) =>
          Documentation.fromJSON(d as TDehydratedDocumentation),
        project_completion: (d) =>
          ProjectCompletion.fromJSON(d as TDehydratedProjectCompletion),
        project_date_completion: (d) =>
          ProjectDateCompletion.fromJSON(d as TDehydratedProjectDateCompletion),
        travel_log: (t) => TravelLog.fromJSON(t as TDehydratedTravelLog),
        video: (v) => Video.fromJSON(v as TDehydratedVideo),
      }
      this.$state = JSON.parse(state, (key, value) => {
        if (key === 'queue') {
          return value.map((item: Sendable) => {
            return hydrationMap[item.type](item)
          })
        } else {
          return value
        }
      })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useServerQueue, import.meta.hot))
}

export { useServerQueue }
